import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// The agent's one-time NIPR result notice (#15266, shared/_nipr_result_notice_modal).
// Opens itself on the page it is rendered on, and any way the agent closes it —
// OK, the X, Escape — or follows Update NPN stamps the notice shown, so it never
// shows again. The POST runs once per page however many of those fire.
//
// A Turbo snapshot must not carry the open modal (a restored page would re-open
// a notice the server no longer has pending), so it is torn down before caching
// WITHOUT acknowledging: leaving the page is not reading the message, and an
// unread notice simply renders again on the next full page.
export default class extends Controller {
  static values = { acknowledgeUrl: String }

  connect() {
    this.acknowledged = null
    this.tearingDown = false
    this.boundBeforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    queueMicrotask(() => this.open())
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  open() {
    const Modal = window.bootstrap?.Modal
    if (!Modal || !this.element.isConnected) return

    this.modal = Modal.getOrCreateInstance(this.element)
    this.modal.show()
  }

  // hide.bs.modal: OK (data-bs-dismiss), the X and Escape all arrive here.
  closing() {
    if (this.tearingDown) return

    void this.acknowledge()
  }

  // hidden.bs.modal: the notice is done on this page; drop it from the DOM so a
  // later snapshot or a stray event cannot bring it back.
  closed() {
    this.modal?.dispose()
    this.modal = null
    this.element.remove()
  }

  // Update NPN: record the close first, then follow the link (the request would
  // be cut off by the navigation otherwise). A failed POST still navigates — the
  // notice shows once more, which is the safe direction.
  async updateNpn(event) {
    event.preventDefault()
    const href = event.currentTarget.getAttribute("href")
    await this.acknowledge()
    this.navigateTo(href)
  }

  // The navigation seam (jsdom cannot spy window.location.assign).
  navigateTo(href) {
    window.location.assign(href)
  }

  acknowledge() {
    if (!this.acknowledged) this.acknowledged = this.send()
    return this.acknowledged
  }

  async send() {
    try {
      const response = await post(this.acknowledgeUrlValue)
      return response.ok
    } catch {
      return false
    }
  }

  teardown() {
    this.tearingDown = true
    this.modal?.hide()
    this.closed()
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
  }
}
